import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { getUserInformation } from "../../store/userRole"; // Import getUserInformation
import Button from "../Common/Button";
import * as yup from "yup";
import { Formik } from "formik";
import PhoneInput from "react-phone-input-2";
import { InputGroup, Form } from "react-bootstrap";
import { toast } from "react-toastify";
import ReCAPTCHA from "react-google-recaptcha";
import "./styles.scss";
import { userRegister } from "../../utils/authService";
import { getPublicClassrooms } from "../../utils/classrooms";
import { getPublicSchools } from "../../utils/signup";
import { consumeStudentRedirectUrl } from "../../utils/redirectStorage";
import LazyLoad from "react-lazyload";
import OtpComponent from "./OtpComponent"; // Import the OTP Component
import { getLiveClassDataById, joinLiveClass } from "../../utils/liveClasses";
import Loader from "../Loader";
import { getToken } from "../../utils/tokenStorage";
import { openLinkInNewTab } from "../../pages/StudentPages/LiveClasses/utils";
import MultiSelectDropdown from "../Classroom/MultiSelectDropdown";
import StyledSelectField from "../Classroom/StyledSelectField";

const registrationSchema = yup.object({
  firstName: yup.string().required("First name is required"),
  school: yup.string().required("School is required"),
  lastName: yup.string().required("Last name is required"),
  phone: yup.string().required("Phone number is required"),
  email: yup
    .string()
    .email("Invalid email address")
    .required("Email is required"),
  classRoomIds: yup
    .array()
    .min(1, "At least one classroom must be selected")
    .required("Classroom is required"),
  password: yup
    .string()
    .required("Password is required")
    .min(8, "Password must be at least 8 characters"),
  confirmPassword: yup
    .string()
    .oneOf([yup.ref("password"), null], "Passwords must match")
    .required("Confirm password is required"),
  terms: yup.bool().oneOf([true], "You must agree to the terms and conditions"),
});

const RegisterForm = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const queryParams = new URLSearchParams(location.search);
  const classroomId = queryParams.get("classroomId");
  const liveclassid = queryParams.get("liveclassid");
  const redirectUrl = queryParams.get("redirect"); // Extract redirect URL from query params

  const userInformation = useSelector(getUserInformation); // Get user information

  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [recaptchaValue, setRecaptchaValue] = useState(null);
  const [classrooms, setClassrooms] = useState([]);
  const [schools, setSchools] = useState([]);
  const [isOtherSchool, setIsOtherSchool] = useState(false);
  const [showOtp, setShowOtp] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState(""); // Store password
  const [otpCompleted, setOtpCompleted] = useState(false); // Track if OTP was just completed
  const [initialValues, setInitialValues] = useState({
    firstName: "",
    lastName: "",
    phone: "",
    email: "",
    classRoomIds: classroomId ? classroomId.split(",") : [], // Store as an array
    password: "",
    confirmPassword: "",
    terms: false,
    school: "",
  });

  useEffect(() => {
    const checkAuthenticationAndNavigate = async () => {
      try {
        const token = await getToken();
        const isAuthenticated = token !== null;

        if (isAuthenticated && userInformation) {
          const role = userInformation.roleName;

          if (liveclassid) {
            joinLiveClassDirectly(liveclassid);
          } else {
            if (role !== "Student") {
              navigate("/myClassrooms");
            } else {
              // Don't redirect if the user just completed OTP
              // This prevents consuming the stored redirect URL that was just used by the OTP success handler
              if (showOtp || otpCompleted) {
                return; // Don't redirect if OTP flow is active or just completed
              }

              // For already authenticated students, check stored URL first, then URL params, then default
              const storedRedirectUrl = consumeStudentRedirectUrl();
              if (storedRedirectUrl) {
                navigate(storedRedirectUrl, { replace: true });
              } else if (redirectUrl !== null) {
                const fullRedirectUrl = `/${redirectUrl}`;
                navigate(fullRedirectUrl, { replace: true });
              } else {
                navigate("/student/myClassrooms", { replace: true });
              }
            }
          }
        }
      } catch (error) {
        console.error("Error checking authentication:", error);
      }
    };

    checkAuthenticationAndNavigate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userInformation, liveclassid]);

  const joinLiveClassDirectly = async (liveclassid) => {
    setLoading(true);
    try {
      const liveClassResponse = await getLiveClassDataById(liveclassid);
      if (liveClassResponse.ok) {
        const { meetingID, attendeePW } = liveClassResponse.data.liveClass;
        const joinResponse = await joinLiveClass(
          liveclassid,
          JSON.stringify({
            meetingID,
            password: attendeePW,
            classroomId: classroomId,
          }),
          "join",
        );
        if (joinResponse.ok && joinResponse.data.url) {
          openLinkInNewTab(joinResponse.data.url);
          navigate("/student/myClassrooms"); // Navigate to dashboard after joining the class
        } else {
          navigate("/student/myClassrooms"); // Navigate to dashboard after joining the class
          // toast.error("Failed to join live class.");
        }
      } else {
        navigate("/student/myClassrooms"); // Navigate to dashboard after joining the class
        toast.error("Failed to fetch live class details.");
      }
    } catch (error) {
      console.error("Error joining live class:", error);
      toast.error("Failed to join live class.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [classroomResponse, schoolResponse] = await Promise.all([
          getPublicClassrooms(),
          getPublicSchools(),
        ]);
        if (classroomResponse.ok) {
          setClassrooms(classroomResponse.data.classRooms);
          if (classroomId && !initialValues.classRoomIds.length) {
            setInitialValues((prevValues) => ({
              ...prevValues,
              classRoomIds: [classroomId],
            }));
          }
        } else {
          console.error("Failed to fetch classrooms", classroomResponse.error);
        }
        if (schoolResponse.ok) {
          setSchools(schoolResponse.data.schools || []);
        }
      } catch (error) {
        console.error("Failed to fetch data", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [classroomId, initialValues.classRoomIds.length]);

  const handleRegister = async (data) => {
    setLoading(true);
    try {
      const registerResponse = await userRegister({
        ...data,
        fullName: `${data.firstName} ${data.lastName}`,
        phone: `+${data.phone}`,
        roleId: "6613bdfdbbae6a9f72f61a62",
        roleName: "Student",
        recaptcha: recaptchaValue,
        classRoomIds: data.classRoomIds,
        school: data.school,
        url: window.location.href,
      });

      if (registerResponse.ok && registerResponse.data.user) {
        setEmail(data.email); // Store the email for OTP verification
        setPassword(data.password); // Store the password for login
        setShowOtp(true); // Show OTP component
      } else {
        toast.error(registerResponse?.error);
      }
    } catch (error) {
      console.error("Registration error:", error);
      toast.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (values) => {
    if (!recaptchaValue) {
      toast.error("Please complete the reCAPTCHA");
      return;
    }
    await handleRegister(values);
  };

  const handleRecaptchaChange = (value) => {
    setRecaptchaValue(value);
  };

  const classroomOptions = classrooms?.map((classroom) => ({
    value: classroom?._id,
    label: classroom?.name,
  }));

  // Check if both classroomId and liveclassid are present in the URL
  const isClassroomDisabled = classroomId?.length || liveclassid;

  // When OTP is successfully verified, redirect to the redirect URL
  const handleOtpVerificationSuccess = (userRole) => {
    // Set flag to prevent the useEffect from interfering with redirect
    setOtpCompleted(true);

    // Define admin/teacher roles that should go to admin interface
    const adminRoles = ["Admin", "SuperAdmin", "Root", "Teacher", "Watcher"];
    const isAdminUser = adminRoles.includes(userRole) || userRole !== "Student";

    if (isAdminUser) {
      // For non-students, clear any stored student redirect URL and use default route
      consumeStudentRedirectUrl(); // Clear the stored URL
      navigate("/myClassrooms", { replace: true });
    } else {
      // For students, check for stored redirect URL first, then URL params, then default
      const storedRedirectUrl = consumeStudentRedirectUrl();

      if (storedRedirectUrl) {
        navigate(storedRedirectUrl, { replace: true });
        // Add a timeout to check if navigation actually happened
        setTimeout(() => {}, 100);
      } else if (redirectUrl !== null && redirectUrl !== undefined) {
        const fullRedirectUrl = redirectUrl.startsWith("/")
          ? redirectUrl
          : `/${redirectUrl}`;
        navigate(fullRedirectUrl, { replace: true });
        setTimeout(() => {}, 100);
      } else {
        navigate("/student/myClassrooms", { replace: true });
      }
    }
  };

  return (
    <Formik
      initialValues={initialValues}
      enableReinitialize // Ensure the form reinitializes when initialValues change
      validationSchema={registrationSchema}
      onSubmit={handleSubmit}
    >
      {({
        values,
        errors,
        touched,
        handleChange,
        handleSubmit,
        handleBlur,
        setFieldValue,
      }) => (
        <div className="right-side">
          {loading && <Loader />}
          {showOtp ? (
            <OtpComponent
              email={email}
              password={password} // Pass the password to OtpComponent
              liveclassid={liveclassid}
              resendOtp={handleSubmit}
              setShowOtp={setShowOtp}
              onOtpSuccess={handleOtpVerificationSuccess} // Callback for OTP verification success
              redirectUrl={redirectUrl} // Pass the redirect URL to OTP component
            />
          ) : (
            <form onSubmit={handleSubmit}>
              <div className="login-heading">
                <h3>Register</h3>
              </div>
              <div className="w-100 d-flex justify-content-between align-items-center gap-3">
                <div className="w-100">
                  <label htmlFor="firstName">First Name</label>
                  <Form.Control
                    name="firstName"
                    type="text"
                    placeholder="Enter your first name"
                    value={values.firstName}
                    onChange={handleChange}
                    onBlur={handleBlur}
                  />
                  <p className="text-danger">
                    {errors.firstName && touched.firstName && errors.firstName}
                  </p>
                </div>
                <div className="w-100">
                  <label htmlFor="lastName">Last Name</label>
                  <Form.Control
                    name="lastName"
                    type="text"
                    placeholder="Enter your last name"
                    value={values.lastName}
                    onChange={handleChange}
                    onBlur={handleBlur}
                  />
                  <p className="text-danger">
                    {errors.lastName && touched.lastName && errors.lastName}
                  </p>
                </div>
              </div>
              <div className="w-100">
                <label htmlFor="phone">Phone Number</label>
                <PhoneInput
                  country={"us"}
                  value={values.phone}
                  onChange={(phone) => setFieldValue("phone", phone)}
                  type="tel"
                  name="phone"
                  excludeCountries={[
                    "by", // Belarus
                    "ly", // Libya
                    "cd", // Democratic Republic of Congo
                    "cg", // Republic of Congo
                    "ye", // Yemen
                    "ss", // South Sudan
                    "so", // Somalia
                    "sd", // Sudan
                    "cf", // Central African Republic
                    "cu", // Cuba
                    "kp", // North Korea
                    "ir", // Iran
                    "sy", // Syria
                  ]}
                />
                <p className="text-danger">
                  {errors.phone && touched.phone && errors.phone}
                </p>
              </div>
              <div className="w-100">
                <label htmlFor="email">Email Address</label>
                <Form.Control
                  name="email"
                  type="email"
                  placeholder="Enter your email address"
                  value={values.email}
                  onChange={handleChange}
                  onBlur={handleBlur}
                />
                <p className="text-danger">
                  {errors.email && touched.email && errors.email}
                </p>
              </div>
              <div className="w-100">
                <StyledSelectField
                  label="Institute/School"
                  isRequired
                  optionsList={[
                    ...schools.map((s) => ({ label: s.name, value: s.name })),
                    { label: "Other", value: "other" },
                  ]}
                  value={isOtherSchool ? "other" : values.school}
                  onChange={(e) => {
                    if (e.value === "other") {
                      setIsOtherSchool(true);
                      setFieldValue("school", "");
                    } else {
                      setIsOtherSchool(false);
                      setFieldValue("school", e.value);
                    }
                  }}
                  placeholder="Select your school / institute"
                  isSearchable
                />
                {isOtherSchool && (
                  <Form.Control
                    className="mt-2"
                    name="school"
                    type="text"
                    placeholder="Enter your school / institute name"
                    value={values.school}
                    onChange={handleChange}
                    onBlur={handleBlur}
                  />
                )}
                <p className="text-danger">
                  {errors.school && touched.school && errors.school}
                </p>
              </div>
              <div className="w-100">
                <label htmlFor="classrooms">Select Classroom</label>
                {classroomId ? (
                  <MultiSelectDropdown
                    name="classRoomIds"
                    optionsList={classroomOptions}
                    value={values.classRoomIds}
                    onChange={(selectedOptions) =>
                      setFieldValue(
                        "classRoomIds",
                        selectedOptions
                          ? selectedOptions.map((option) => option.value)
                          : [],
                      )
                    }
                    onBlur={handleBlur}
                    disabled={isClassroomDisabled} // Disable field if classroomId and liveclassid are present
                  />
                ) : (
                  <StyledSelectField
                    name="classRoomIds"
                    optionsList={classroomOptions}
                    value={
                      values.classRoomIds.length > 0
                        ? values.classRoomIds[0]
                        : ""
                    } // Fallback to an empty string if no value is selected
                    onChange={
                      (selectedOption) =>
                        setFieldValue(
                          "classRoomIds",
                          selectedOption ? [selectedOption.value] : [],
                        ) // Store the value as an array
                    }
                    onBlur={handleBlur}
                    disabled={isClassroomDisabled}
                  />
                )}
                <p className="text-danger">
                  {errors.classRoomIds &&
                    touched.classRoomIds &&
                    errors.classRoomIds}
                </p>
              </div>

              <div className="w-100 d-flex justify-content-between align-items-center gap-3">
                <div className="w-100">
                  <Form.Group controlId="password">
                    <Form.Label>Password</Form.Label>
                    <InputGroup>
                      <Form.Control
                        name="password"
                        type={showPassword ? "text" : "password"}
                        placeholder="Enter your password"
                        value={values.password}
                        onChange={handleChange}
                        onBlur={handleBlur}
                      />
                      <InputGroup.Text style={{ background: "transparent" }}>
                        <div
                          className="password-toggle-icon"
                          onClick={() => setShowPassword(!showPassword)}
                        >
                          <LazyLoad height={20} offset={100}>
                            <img
                              src={
                                showPassword
                                  ? "password-eye-slash.svg"
                                  : "password-eye.svg"
                              }
                              alt="showPassword"
                            />
                          </LazyLoad>
                        </div>
                      </InputGroup.Text>
                    </InputGroup>
                  </Form.Group>
                  <p className="text-danger">
                    {errors.password && touched.password && errors.password}
                  </p>
                </div>
                <div className="w-100">
                  <Form.Group controlId="confirmPassword">
                    <Form.Label>Confirm Password</Form.Label>
                    <InputGroup>
                      <Form.Control
                        name="confirmPassword"
                        type={showPassword ? "text" : "password"}
                        placeholder="Confirm your password"
                        value={values.confirmPassword}
                        onChange={handleChange}
                        onBlur={handleBlur}
                      />
                      <InputGroup.Text style={{ background: "transparent" }}>
                        <div
                          className="password-toggle-icon"
                          onClick={() => setShowPassword(!showPassword)}
                        >
                          <LazyLoad height={20} offset={100}>
                            <img
                              src={
                                showPassword
                                  ? "password-eye-slash.svg"
                                  : "password-eye.svg"
                              }
                              alt="showPassword"
                            />
                          </LazyLoad>
                        </div>
                      </InputGroup.Text>
                    </InputGroup>
                  </Form.Group>
                  <p className="text-danger">
                    {errors.confirmPassword &&
                      touched.confirmPassword &&
                      errors.confirmPassword}
                  </p>
                </div>
              </div>
              <div className="row w-100 mt-3 mx-1">
                <div className="col-sm-12 px-0">
                  <Form.Check
                    type="checkbox"
                    name="terms"
                    label="I agree to all of your terms and conditions"
                    checked={values.terms}
                    onChange={handleChange}
                    onBlur={handleBlur}
                  />
                </div>
                <div className="col-sm-12 px-0 mt-3">
                  <ReCAPTCHA
                    sitekey={
                      process.env.REACT_APP_RECAPTCHA_SITE_KEY ||
                      "6Leu5N8pAAAAAG9yplwN-rMtOn8pjYO8FhW_W0LU"
                    }
                    onChange={handleRecaptchaChange}
                  />
                  <p className="text-danger mt-1">
                    {errors.recaptcha && touched.recaptcha && errors.recaptcha}
                  </p>
                </div>
                <div className="col-sm-12 px-0 d-flex justify-content-end mt-2">
                  <Button
                    disabled={loading}
                    className="login-btn"
                    type="submit"
                    onClick={handleSubmit}
                  >
                    <span>
                      {loading ? "Creating account..." : "Create Account"}
                    </span>
                    <LazyLoad height={20} offset={100}>
                      <img src="/ArrowRight.png" alt="arrow" />
                    </LazyLoad>
                  </Button>
                </div>
              </div>
              <p className="text-danger">
                {errors.terms && touched.terms && errors.terms}
              </p>
            </form>
          )}
        </div>
      )}
    </Formik>
  );
};

export default RegisterForm;
